import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Car, LogOut, RefreshCw, Phone, CreditCard, Banknote, CalendarDays, Copy, Check, QrCode, Gift, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [calLink, setCalLink] = useState("");
  const [calCopied, setCalCopied] = useState(false);
  const [reviewUrl, setReviewUrl] = useState("");
  const [reviewQr, setReviewQr] = useState(null);
  const [savingReview, setSavingReview] = useState(false);
  const [loyalty, setLoyalty] = useState([]);

  useEffect(() => {
    if (location.state?.user) return;
    if (window.location.hash?.includes("session_id=")) return;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setChecking(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const res = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(res.data);
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    } finally {
      setLoadingBookings(false);
    }
  };

  const markDone = async (id) => {
    try {
      await axios.patch(`${API}/bookings/${id}/status`, { status: "completed" }, { withCredentials: true });
      loadBookings();
      loadLoyalty();
    } catch { /* ignore */ }
  };

  const loadReview = async () => {
    try {
      const r = await axios.get(`${API}/settings/review-link`, { withCredentials: true });
      setReviewUrl(r.data.url || "");
      setReviewQr(r.data.qr_png_base64 || null);
    } catch { /* ignore */ }
  };

  const loadLoyalty = async () => {
    try {
      const r = await axios.get(`${API}/loyalty`, { withCredentials: true });
      setLoyalty(r.data);
    } catch { /* ignore */ }
  };

  const saveReview = async () => {
    if (!reviewUrl.trim()) return;
    setSavingReview(true);
    try {
      await axios.put(`${API}/settings/review-link`, { url: reviewUrl.trim() }, { withCredentials: true });
      await loadReview();
      toast.success("Lien d'avis enregistré — QR code mis à jour.");
    } catch {
      toast.error("Lien invalide — il doit commencer par https://");
    } finally {
      setSavingReview(false);
    }
  };

  const redeemReward = async (phone) => {
    try {
      await axios.post(`${API}/loyalty/redeem`, { phone }, { withCredentials: true });
      toast.success("Récompense offerte — compteur mis à jour.");
      loadLoyalty();
    } catch {
      toast.error("Aucune récompense disponible pour ce client.");
    }
  };

  useEffect(() => {
    if (user) {
      loadBookings();
      loadReview();
      loadLoyalty();
      axios.get(`${API}/calendar/link`, { withCredentials: true }).then((r) => setCalLink(r.data.url)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
      setBookings([]);
    }
  };

  if (checking) {
    return (
      <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-champagne border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login" className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-surface border border-amber-600/20 rounded-lg p-10 max-w-sm w-full text-center shadow-[0_30px_80px_rgba(0,0,0,0.08)]">
          <span className="w-12 h-12 rounded-full border border-amber-600/40 flex items-center justify-center text-champagne mx-auto">
            <Car size={20} strokeWidth={1.5} />
          </span>
          <h1 className="font-serif text-2xl text-ivory mt-6">Espace pro <span className="italic text-champagne">Kenny Cab</span></h1>
          <p className="text-sm text-zinc-600 mt-3">Connectez-vous pour consulter vos réservations.</p>
          <button
            data-testid="admin-login-btn"
            onClick={login}
            className="mt-8 w-full flex items-center justify-center gap-3 bg-ivory text-obsidian text-sm font-semibold rounded-full px-6 py-3.5 hover:shadow-[0_0_30px_rgba(154,123,36,0.25)] transition-all duration-300"
          >
            <span className="w-5 h-5 rounded-full bg-champagne text-white flex items-center justify-center text-xs font-bold">G</span>
            Continuer avec Google
          </button>
          <a data-testid="admin-back-home" href="/" className="block mt-6 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-champagne transition-colors">
            ← Retour au site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen px-4 sm:px-8 lg:px-16 py-10 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full border border-amber-600/40 flex items-center justify-center text-champagne">
            <Car size={17} strokeWidth={1.5} />
          </span>
          <div>
            <h1 className="font-serif text-2xl text-ivory">Réservations <span className="italic text-champagne">Kenny Cab</span></h1>
            <p data-testid="admin-user-email" className="text-xs text-zinc-500">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-refresh-btn" onClick={loadBookings} className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-champagne border border-amber-600/20 hover:border-amber-600/50 rounded-full px-4 py-2.5 transition-colors">
            <RefreshCw size={13} /> Actualiser
          </button>
          <a data-testid="admin-home-link" href="/" className="text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-champagne border border-amber-600/20 hover:border-amber-600/50 rounded-full px-4 py-2.5 transition-colors">Site</a>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-red-600 border border-amber-600/20 hover:border-red-400/50 rounded-full px-4 py-2.5 transition-colors">
            <LogOut size={13} /> Déconnexion
          </button>
        </div>
      </div>

      {calLink && (
        <div data-testid="calendar-feed-card" className="mt-8 bg-surface border border-amber-600/20 rounded-lg p-6 sm:p-8">
          <h2 className="font-serif text-xl text-ivory flex items-center gap-3">
            <CalendarDays size={20} strokeWidth={1.5} className="text-champagne" /> Synchroniser mon Google Agenda
          </h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl leading-relaxed">
            Toutes vos courses apparaissent automatiquement dans votre agenda. Dans Google Agenda (sur calendar.google.com) :
            <strong> Autres agendas → + → À partir de l'URL</strong> → collez ce lien :
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <code data-testid="calendar-feed-url" className="flex-1 text-xs font-mono bg-obsidian border border-amber-600/20 rounded-md px-4 py-3 text-champagne break-all">{calLink}</code>
            <button data-testid="calendar-copy-btn"
              onClick={() => { navigator.clipboard.writeText(calLink); setCalCopied(true); setTimeout(() => setCalCopied(false), 2000); }}
              className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-champagne text-white rounded-full px-6 py-3 hover:bg-champagne-bright transition-colors whitespace-nowrap">
              {calCopied ? <><Check size={14} /> Copié !</> : <><Copy size={14} /> Copier le lien</>}
            </button>
            <a data-testid="calendar-open-google-btn" href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-zinc-700 border border-amber-600/30 rounded-full px-6 py-3 hover:border-amber-600/60 hover:text-champagne transition-colors whitespace-nowrap">
              <CalendarDays size={14} /> Ouvrir Google Agenda
            </a>
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">Lien privé — ne le partagez pas, il contient vos réservations. Google actualise l'agenda plusieurs fois par jour ; l'email de notification reste instantané.</p>
        </div>
      )}

      <div data-testid="review-qr-card" className="mt-8 bg-surface border border-amber-600/20 rounded-lg p-6 sm:p-8">
        <h2 className="font-serif text-xl text-ivory flex items-center gap-3">
          <QrCode size={20} strokeWidth={1.5} className="text-champagne" /> QR Code avis Google
        </h2>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl leading-relaxed">
          Collez le lien « Donner un avis » de votre fiche Google Business Profile (Google Maps → votre fiche → Partager → Avis).
          Le QR code se génère automatiquement : montrez-le au client en fin de course ou imprimez-le dans le véhicule.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="review-url-input"
            type="url"
            value={reviewUrl}
            onChange={(e) => setReviewUrl(e.target.value)}
            placeholder="https://g.page/r/…/review"
            className="flex-1 text-sm bg-obsidian border border-amber-600/20 rounded-md px-4 py-3 text-ivory placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 transition-colors"
          />
          <button data-testid="review-save-btn" onClick={saveReview} disabled={savingReview}
            className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-champagne text-white rounded-full px-6 py-3 hover:bg-champagne-bright transition-colors whitespace-nowrap disabled:opacity-60">
            {savingReview ? "Enregistrement…" : "Générer le QR code"}
          </button>
        </div>
        {reviewQr && (
          <div className="mt-6 flex flex-col sm:flex-row items-center gap-6">
            <div className="bg-white border border-amber-600/20 rounded-lg p-4 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
              <img data-testid="review-qr-image" src={`data:image/png;base64,${reviewQr}`} alt="QR code avis Google Kenny Cab" className="w-44 h-44" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-sm text-zinc-600 max-w-xs leading-relaxed">Prêt à l'emploi : le client scanne, la page d'avis Google s'ouvre, il note en 10 secondes.</p>
              <a data-testid="review-download-btn" href={`data:image/png;base64,${reviewQr}`} download="qr-avis-google-kennycab.png"
                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-zinc-700 border border-amber-600/30 rounded-full px-6 py-3 hover:border-amber-600/60 hover:text-champagne transition-colors">
                <Download size={14} /> Télécharger le QR (PNG)
              </a>
            </div>
          </div>
        )}
      </div>

      <div data-testid="loyalty-card" className="mt-8 bg-surface border border-amber-600/20 rounded-lg p-6 sm:p-8">
        <h2 className="font-serif text-xl text-ivory flex items-center gap-3">
          <Gift size={20} strokeWidth={1.5} className="text-champagne" /> Programme fidélité
        </h2>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl leading-relaxed">
          10 courses effectuées = 1 course en ville offerte. Les points sont crédités automatiquement (par n° de téléphone)
          quand vous marquez une course « effectuée ».
        </p>
        {loyalty.length === 0 ? (
          <p data-testid="loyalty-empty" className="mt-4 text-sm text-zinc-500">Aucun client fidélisé pour le moment — les points apparaîtront après vos premières courses marquées « effectuées ».</p>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table data-testid="loyalty-table" className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-amber-600/10">
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Courses</th>
                  <th className="px-4 py-3">Progression</th>
                  <th className="px-4 py-3">Récompense</th>
                </tr>
              </thead>
              <tbody>
                {loyalty.map((c) => (
                  <tr key={c.phone} data-testid={`loyalty-row-${c.phone}`} className="border-b border-amber-600/5">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-ivory">{c.name || "—"}</div>
                      <a href={`tel:${c.phone}`} className="text-xs text-zinc-500 hover:text-champagne">{c.phone}</a>
                    </td>
                    <td className="px-4 py-3 text-zinc-700 font-mono">{c.rides}</td>
                    <td className="px-4 py-3 w-40">
                      <div className="h-1.5 bg-obsidian rounded-full overflow-hidden border border-amber-600/15">
                        <div className="h-full bg-champagne rounded-full transition-all duration-500" style={{ width: `${(c.progress / 10) * 100}%` }} />
                      </div>
                      <span className="text-[11px] text-zinc-500 mt-1 block">{c.progress}/10</span>
                    </td>
                    <td className="px-4 py-3">
                      {c.rewards_available > 0 ? (
                        <button data-testid={`loyalty-redeem-${c.phone}`} onClick={() => redeemReward(c.phone)}
                          className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-full px-3 py-1.5 whitespace-nowrap transition-colors">
                          Offrir la course ({c.rewards_available})
                        </button>
                      ) : (
                        <span className="text-[11px] uppercase tracking-wider text-zinc-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {forbidden && (
        <p data-testid="admin-forbidden" className="mt-8 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">
          Ce compte Google n'est pas autorisé à voir les réservations. Contactez l'administrateur.
        </p>
      )}

      <div className="mt-10 bg-surface border border-amber-600/15 rounded-lg overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.06)]">
        <div className="overflow-x-auto">
          <table data-testid="bookings-table" className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-amber-600/10">
                <th className="px-5 py-4">Réf.</th>
                <th className="px-5 py-4">Client</th>
                <th className="px-5 py-4">Trajet</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Pax/Bag.</th>
                <th className="px-5 py-4">Paiement</th>
                <th className="px-5 py-4">Statut</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && !loadingBookings && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500" data-testid="bookings-empty">Aucune réservation pour le moment.</td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.reference} data-testid={`booking-row-${b.reference}`} className="border-b border-amber-600/5 hover:bg-surface-hover transition-colors">
                  <td className="px-5 py-4 font-mono text-champagne whitespace-nowrap">{b.reference}</td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-ivory">{b.name}</div>
                    <a href={`tel:${b.phone}`} className="text-xs text-zinc-500 hover:text-champagne flex items-center gap-1 mt-0.5"><Phone size={10} />{b.phone}</a>
                  </td>
                  <td className="px-5 py-4 text-zinc-700">
                    <div>{b.pickup}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">→ {b.destination}</div>
                    {b.notes && <div className="text-xs text-zinc-500 italic mt-1">{b.notes}</div>}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap text-zinc-700">{b.date}<span className="text-zinc-500"> · {b.time}</span></td>
                  <td className="px-5 py-4 text-zinc-700 whitespace-nowrap">{b.passengers} / {b.luggage}</td>
                  <td className="px-5 py-4">
                    {b.payment_status === "paid" && b.payment_method === "deposit" ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 whitespace-nowrap"><CreditCard size={11} /> Acompte 25 % payé</span>
                    ) : b.payment_status === "paid" ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 whitespace-nowrap"><CreditCard size={11} /> Payée en ligne</span>
                    ) : (b.payment_method === "online" || b.payment_method === "deposit") ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-3 py-1 whitespace-nowrap"><CreditCard size={11} /> En attente d'acompte</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-zinc-700 bg-zinc-100 border border-zinc-300 rounded-full px-3 py-1 whitespace-nowrap"><Banknote size={11} /> Espèces à bord</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {b.status === "completed" ? (
                      <span data-testid={`status-badge-${b.reference}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 whitespace-nowrap">✓ Effectuée</span>
                    ) : (
                      <button data-testid={`status-btn-${b.reference}`} onClick={() => markDone(b.id)} className="text-[11px] uppercase tracking-wider font-semibold text-zinc-600 hover:text-champagne border border-amber-600/20 hover:border-amber-600/60 rounded-full px-3 py-1.5 whitespace-nowrap transition-colors">Marquer effectuée</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
